import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Send, UserCog } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { Sparkline } from '../components/shared/Sparkline';
import {
  BandChip,
  NotifyParentsDrawer,
  PeriodReports,
  ProbationHistoryPanel,
  ScoreCard,
  VariableBreakdown,
  type PerformanceLeaderboardResponse,
  type PerformanceSnapshotEnvelope,
  type PerformanceTrendResponse,
} from '../components/performance';
import { formatDate } from '../lib/format';

export const Route = createFileRoute('/_authed/me/performance')({
  component: MyPerformancePage,
});

function MyPerformancePage() {
  const user = useAuthStore((s) => s.user);

  // The /me/performance route is teacher-only. Other roles see a guided
  // empty state pointing them at the right surface (HR sees the
  // leaderboard view, teachers see this view, everyone else sees nothing).
  if (user && user.primaryRole !== 'teacher') {
    return (
      <ListShell
        title="My performance"
        description="This page is for teachers only."
      >
        <EmptyState
          title="No personal performance score for this role"
          description={
            user.primaryRole === 'nalim' ||
            user.primaryRole === 'academic_admin' ||
            user.primaryRole === 'super_admin'
              ? 'You can review every teacher\'s performance from the HR view.'
              : 'Performance scores are only computed for teachers.'
          }
          icon={<LineChart size={32} aria-hidden="true" />}
          action={
            user.primaryRole === 'nalim' ||
            user.primaryRole === 'academic_admin' ||
            user.primaryRole === 'super_admin' ? (
              <Link to="/hr/performance">
                <Button variant="secondary">Open performance scores</Button>
              </Link>
            ) : undefined
          }
        />
      </ListShell>
    );
  }

  return <TeacherPerformanceView />;
}

function TeacherPerformanceView() {
  const user = useAuthStore((s) => s.user);
  const teacherId = user?.teacherId ?? null;
  const teacherName = user?.displayName ?? 'Teacher';
  const teacherProfileMissing = Boolean(user) && !teacherId;
  const [notifyOpen, setNotifyOpen] = useState(false);

  const snapshotQuery = useQuery({
    queryKey: ['performance', 'snapshot', teacherId] as const,
    enabled: Boolean(teacherId),
    queryFn: ({ signal }) =>
      apiRequest<PerformanceSnapshotEnvelope>(
        `/api/performance/teachers/${encodeURIComponent(teacherId!)}/snapshot`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const trendQuery = useQuery({
    queryKey: ['performance', 'trend', teacherId] as const,
    enabled: Boolean(teacherId),
    queryFn: ({ signal }) =>
      apiRequest<PerformanceTrendResponse>(
        `/api/performance/teachers/${encodeURIComponent(teacherId!)}/trend`,
        { signal, query: { days: 30 } },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // Comparison line: only when the user has HR permission AND the
  // leaderboard responds successfully. We never block the page on this;
  // it's purely a nice-to-have side comparison. Teachers don't have
  // access to /leaderboard, so this query is short-circuited via
  // `enabled: false` for them.
  const isHrPlus = useAuthStore((s) =>
    s.hasRole('nalim', 'academic_admin', 'super_admin'),
  );
  const academyAverageQuery = useQuery({
    queryKey: ['performance', 'leaderboard', { pageSize: 100 }] as const,
    enabled: Boolean(teacherId) && isHrPlus,
    queryFn: ({ signal }) =>
      apiRequest<PerformanceLeaderboardResponse>(
        '/api/performance/leaderboard',
        { signal, query: { pageSize: 100 } },
      ),
    retry: false,
  });

  const academyAverage = useMemo<number | null>(() => {
    const items = academyAverageQuery.data?.items;
    if (!items || items.length === 0) return null;
    const sum = items.reduce((acc, r) => acc + r.score, 0);
    return Math.round(sum / items.length);
  }, [academyAverageQuery.data]);

  return (
    <ListShell
      title="My performance"
      description={`Your score this period for ${teacherName}. A snapshot of how the last 90 days are tracking — not a job evaluation.`}
    >
      <GatedSection
        error={snapshotQuery.error}
        title="My performance"
        description="Once the TMA module is enabled by the academy, you'll see your score and breakdown here."
        onRetry={() => {
          void snapshotQuery.refetch();
          void trendQuery.refetch();
        }}
      >
        {teacherProfileMissing ? (
          <EmptyState
            title="Teacher profile not linked yet"
            description="Your account is marked as a teacher but is not yet linked to a teacher record. Please contact the academy office so your performance snapshot can be shown here."
            icon={<UserCog size={32} aria-hidden="true" />}
          />
        ) : !teacherId ? (
          <Skeleton className="h-64 w-full" />
        ) : snapshotQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : !snapshotQuery.data?.snapshot ? (
          // No TMA snapshot yet — but an onboarding teacher may already have
          // weekly probation reviews recorded. Surface that history (M-6)
          // beneath the "first snapshot on the way" card. No admin link: this
          // is the teacher's own view, never the HR write surface.
          <div className="space-y-6">
            <NoSnapshotYet />
            <ProbationHistoryPanel probation={snapshotQuery.data?.probation} />
          </div>
        ) : (
          <div className="space-y-6">
            <ScoreCard
              snapshot={snapshotQuery.data.snapshot}
              academyAverage={academyAverage}
            />

            {/* Onboarding period (M-6) — teacher-self view, no admin link. */}
            <ProbationHistoryPanel probation={snapshotQuery.data?.probation} />

            <section
              className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5"
              aria-labelledby="breakdown-heading"
            >
              <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h2
                  id="breakdown-heading"
                  className="font-display text-base font-semibold"
                >
                  Breakdown by metric
                </h2>
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  Each row shows the raw value, the 0–100 score, and the
                  weighted contribution to your total.
                </p>
              </header>
              <VariableBreakdown metrics={snapshotQuery.data.snapshot.metrics} />
            </section>

            <section
              className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5"
              aria-labelledby="trend-heading"
            >
              <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h2
                  id="trend-heading"
                  className="font-display text-base font-semibold"
                >
                  Last 30 days
                </h2>
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  One mark per nightly snapshot. Gaps mean no snapshot was
                  written that day.
                </p>
              </header>
              {trendQuery.isLoading || !trendQuery.data ? (
                <Skeleton className="h-32 w-full" />
              ) : trendQuery.data.items.length === 0 ? (
                <p className="text-sm text-[color:var(--color-text-muted)]">
                  No trend data yet — your first nightly snapshot will
                  populate this chart.
                </p>
              ) : (
                <Sparkline
                  points={trendQuery.data.items
                    .slice()
                    .sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate))
                    .map((item) => ({
                      x: item.snapshotDate,
                      y: item.score,
                      title: `${formatDate(item.snapshotDate)} · ${item.score}/100 · ${item.band}`,
                    }))}
                  gridlines={[33, 66]}
                  ariaLabel="Your score across the last 30 days"
                  srCaption={`Trend across the last ${trendQuery.data.items.length} snapshots, range 0 to 100.`}
                />
              )}
            </section>

            {teacherId ? <PeriodReports teacherId={teacherId} /> : null}

            <section className="rounded-xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] p-5 dark:from-gold-700/15">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-display text-base font-semibold">
                    Reach out to parents
                  </h2>
                  <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                    Need to nudge families about overdue fees, attendance, or
                    quiz participation? Send a focused message to just that
                    group. Limit: once per audience per day.
                  </p>
                </div>
                <Button
                  variant="primary"
                  icon={<Send size={14} aria-hidden="true" />}
                  onClick={() => setNotifyOpen(true)}
                >
                  Send message to parents
                </Button>
              </div>
            </section>

            <NotifyParentsDrawer
              open={notifyOpen}
              teacherId={teacherId}
              onClose={() => setNotifyOpen(false)}
            />
          </div>
        )}
      </GatedSection>
    </ListShell>
  );
}

function NoSnapshotYet() {
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-6 py-10 text-center">
      <div className="mx-auto flex max-w-md flex-col items-center">
        <div className="rounded-full bg-gold-50 p-3 text-gold-700 dark:bg-gold-700/20 dark:text-gold-100">
          <LineChart size={28} aria-hidden="true" />
        </div>
        <h2 className="mt-4 font-display text-xl font-semibold">
          Your first snapshot is on the way
        </h2>
        <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
          Snapshots are written nightly. Once the first one lands, your
          score and breakdown will appear here. Newer teachers usually
          spend a few weeks in the &ldquo;warming up&rdquo; band before a
          full ranking is published.
        </p>
        <div className="mt-3">
          <BandChip band="warming_up" />
        </div>
      </div>
    </section>
  );
}
